import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle, Clock, XCircle, Ban, FileText, DollarSign, TrendingUp, TrendingDown, Percent } from "lucide-react";
import { Card, CardContent, CircularProgress, Divider } from "@mui/material";
import { getRequest } from "../../ApiFunction";
import API, { BASE_URL } from "../../Api";
import { toast } from "react-toastify";

const FSP_PENDING_REQUEST_TYPES = new Set([
    "LOAN_OFFER_REQUEST",
    "TOP_UP_OFFER_REQUEST",
    "LOAN_RESTRUCTURE_REQUEST",
    "LOAN_TAKEOVER_OFFER_REQUEST",
]);

const EMPLOYER_PENDING_MESSAGE_TYPE = "LOAN_INITIAL_APPROVAL_NOTIFICATION";
const EMPLOYER_PENDING_RESPONSE_CODE = "800";

const getLoanRequestType = (loan) => {
    const requestType =
        loan?.requestType ||
        loan?.messageType ||
        loan?.latestMessageType ||
        loan?.MessageType ||
        "";

    return String(requestType).trim().toUpperCase();
};

const getLoanResponseCode = (loan) => {
    const responseCode =
        loan?.responseCode ||
        loan?.latestResponseCode ||
        loan?.essResponseCode ||
        loan?.ResponseCode ||
        loan?.response?.responseCode ||
        loan?.response?.ResponseCode ||
        loan?.responseDetails?.ResponseCode ||
        loan?.responseDetails?.responseCode ||
        loan?.latestMessageDetails?.ResponseCode ||
        "";

    return String(responseCode).trim();
};

const Dashboard = () => {
    const [summary, setSummary] = useState({
        pendingEmployer: 0,
        pendingFsp: 0,
        active: 0,
        cancelled: 0,
        rejected: 0,
        closed: 0,
    });
    const [financialMetrics, setFinancialMetrics] = useState({
        totalPortfolio: 0,
        totalDisbursed: 0,
        outstandingBalance: 0,
        totalCollected: 0,
        defaultRate: 0,
        averageLoanSize: 0,
    });
    const [loading, setLoading] = useState(true);

    const fetchDashboardStats = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getRequest(`${BASE_URL}${API.ALL_EMPLOYEES_LOAN}`);
            const { success, data, message } = result.data;

            if (!success) {
                toast.error(message || "Failed to fetch dashboard statistics");
                return;
            }

            // Calculate statistics from loans data
            const loans = data?.loans || [];
            
            // Status counts
            const stats = {
                pendingEmployer: loans.filter(l => 
                    getLoanRequestType(l) === EMPLOYER_PENDING_MESSAGE_TYPE &&
                    getLoanResponseCode(l) === EMPLOYER_PENDING_RESPONSE_CODE
                ).length,
                pendingFsp: loans.filter(l => 
                    FSP_PENDING_REQUEST_TYPES.has(getLoanRequestType(l))
                ).length,
                active: loans.filter(l => 
                    l.status === 'ACTIVE' || 
                    l.status === 'DISBURSED' ||
                    l.status?.toLowerCase() === 'active'
                ).length,
                cancelled: loans.filter(l => 
                    l.status === 'CANCELLED' ||
                    l.status?.toLowerCase() === 'cancelled'
                ).length,
                rejected: loans.filter(l => 
                    l.status === 'REJECTED' ||
                    l.status?.toLowerCase() === 'rejected'
                ).length,
                closed: loans.filter(l => 
                    l.status === 'CLOSED' || 
                    l.status === 'COMPLETED' ||
                    l.status?.toLowerCase() === 'closed'
                ).length,
            };

            // Financial calculations
            const activeLoans = loans.filter(l => 
                l.status === 'ACTIVE' || 
                l.status === 'DISBURSED' ||
                l.status?.toLowerCase() === 'active'
            );
            
            const disbursedLoans = loans.filter(l => 
                l.status === 'DISBURSED' || 
                l.status === 'ACTIVE' || 
                l.status === 'CLOSED' ||
                l.status?.toLowerCase() === 'active' ||
                l.status?.toLowerCase() === 'closed' ||
                l.status?.toLowerCase() === 'disbursed'
            );

            const defaultLoans = loans.filter(l => 
                l.status?.toLowerCase().includes('default') ||
                l.status?.toLowerCase().includes('overdue')
            );

            const totalPortfolio = activeLoans.reduce((sum, loan) => 
                sum + (parseFloat(loan.requestedAmount) || 0), 0
            );

            const totalDisbursed = disbursedLoans.reduce((sum, loan) => 
                sum + (parseFloat(loan.requestedAmount) || 0), 0
            );

            // Outstanding balance (simplified - actual would need repayment data)
            const outstandingBalance = activeLoans.reduce((sum, loan) => 
                sum + (parseFloat(loan.requestedAmount) || 0), 0
            );

            // Total collected (simplified - would need actual repayment records)
            const totalCollected = totalDisbursed - outstandingBalance;

            const defaultRate = loans.length > 0 
                ? ((defaultLoans.length / loans.length) * 100).toFixed(2)
                : 0;

            const averageLoanSize = loans.length > 0
                ? (loans.reduce((sum, loan) => sum + (parseFloat(loan.requestedAmount) || 0), 0) / loans.length)
                : 0;

            const financial = {
                totalPortfolio,
                totalDisbursed,
                outstandingBalance,
                totalCollected: Math.max(0, totalCollected),
                defaultRate: parseFloat(defaultRate),
                averageLoanSize,
            };

            setSummary(stats);
            setFinancialMetrics(financial);
        } catch (err) {
            toast.error(err.message || "Error fetching dashboard data");
            console.error("Dashboard error:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardStats();
    }, [fetchDashboardStats]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-TZ', {
            style: 'currency',
            currency: 'TZS',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const statusItems = [
        {
            title: "Pending Employer Approval",
            value: summary.pendingEmployer,
            icon: <Clock className="text-yellow-500" size={28} />,
        },
        {
            title: "Pending FSP Approval",
            value: summary.pendingFsp,
            icon: <Clock className="text-orange-500" size={28} />,
        },
        {
            title: "Active Loans",
            value: summary.active,
            icon: <CheckCircle className="text-green-600" size={28} />,
        },
        {
            title: "Cancelled",
            value: summary.cancelled,
            icon: <Ban className="text-gray-500" size={28} />,
        },
        {
            title: "Rejected",
            value: summary.rejected,
            icon: <XCircle className="text-red-500" size={28} />,
        },
        {
            title: "Closed (Fully Repaid)",
            value: summary.closed,
            icon: <FileText className="text-blue-600" size={28} />,
        },
    ];

    const financialItems = [
        {
            title: "Total Loan Portfolio",
            value: formatCurrency(financialMetrics.totalPortfolio),
            icon: <DollarSign className="text-purple-600" size={28} />,
            description: "Sum of all active loans"
        },
        {
            title: "Total Disbursed",
            value: formatCurrency(financialMetrics.totalDisbursed),
            icon: <TrendingUp className="text-blue-600" size={28} />,
            description: "Total amount disbursed"
        },
        {
            title: "Outstanding Balance",
            value: formatCurrency(financialMetrics.outstandingBalance),
            icon: <DollarSign className="text-orange-600" size={28} />,
            description: "Remaining loan balances"
        },
        {
            title: "Total Collected",
            value: formatCurrency(financialMetrics.totalCollected),
            icon: <TrendingDown className="text-green-600" size={28} />,
            description: "Repayments received"
        },
        {
            title: "Default Rate",
            value: `${financialMetrics.defaultRate}%`,
            icon: <Percent className="text-red-600" size={28} />,
            description: "Loans in default/overdue"
        },
        {
            title: "Average Loan Size",
            value: formatCurrency(financialMetrics.averageLoanSize),
            icon: <DollarSign className="text-indigo-600" size={28} />,
            description: "Mean loan amount"
        },
    ];

    if (loading) {
        return (
            <div className="container mx-auto p-6 d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6">
            <h5 className="mb-3">Dashboard</h5>
            
            {/* Financial Metrics Section */}
            <div className="mb-5">
                <h6 className="mb-3 text-muted">Financial Overview</h6>
                <div className="row">
                    {financialItems.map((item, idx) => (
                        <div className="col-md-6 col-lg-4 mb-3" key={`financial-${idx}`}>
                            <Card sx={{boxShadow: 'rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.1) 0px 0px 8px', height: '100%'}}>
                                <CardContent className="p-4">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="flex-grow-1">
                                            <p className="text-sm text-muted mb-1">{item.title}</p>
                                            <h4 className="mb-0 fw-bold">{item.value}</h4>
                                            <small className="text-muted">{item.description}</small>
                                        </div>
                                        <div>{item.icon}</div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>

            <Divider className="my-4" />

            {/* Loan Status Section */}
            <div>
                <h6 className="mb-3 text-muted">Loan Status Overview</h6>
                <div className="row">
                    {statusItems.map((item, idx) => (
                        <div className="col-md-6 col-lg-4 mb-3" key={`status-${idx}`}>
                            <Card sx={{boxShadow: 'rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.1) 0px 0px 8px'}}>
                                <CardContent className="d-flex justify-content-between align-items-center p-4">
                                    <div>
                                        <p className="text-sm text-muted mb-1">{item.title}</p>
                                        <h3 className="mb-0 fw-semibold">{item.value}</h3>
                                    </div>
                                    {item.icon}
                                </CardContent>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
